import {
  OBSERVATION_IMAGE_MAX_BYTE_SIZE,
  isObservationImageContentType,
  type ObservationImageContentType,
  type ObservationImageData,
} from "../../../shared/observation-types";

interface UploadIntentResponse {
  data?: {
    uploads?: Array<{
      id: string;
      uploadUrl: string;
      method: "PUT";
      headers: { "Content-Type": ObservationImageContentType };
      expiresInSeconds: number;
    }>;
  };
}

interface CompleteImagesResponse {
  data?: ObservationImageData[];
}

export function observationImagesPath(rdId: string, noteId: string): string {
  return `/api/rds/${encodeURIComponent(rdId)}/observations/${encodeURIComponent(noteId)}/images`;
}

export function observationImagePath(
  rdId: string,
  noteId: string,
  imageId: string,
): string {
  return `${observationImagesPath(rdId, noteId)}/${encodeURIComponent(imageId)}`;
}

export function validateObservationImageFile(file: File): string | null {
  if (!isObservationImageContentType(file.type)) {
    return "Images must be JPG, PNG, or WebP.";
  }
  if (file.size <= 0) {
    return "Images cannot be empty.";
  }
  if (file.size > OBSERVATION_IMAGE_MAX_BYTE_SIZE) {
    return "Images must be 5MB or smaller.";
  }
  return null;
}

export function formatObservationImageByteSize(byteSize: number): string {
  if (byteSize < 1024 * 1024) {
    return `${Math.max(1, Math.round(byteSize / 1024)).toLocaleString()} KB`;
  }
  return `${(byteSize / (1024 * 1024)).toFixed(1)} MB`;
}

export async function readApiError(
  response: Response,
  fallback: string,
): Promise<string> {
  const body = (await response.json().catch(() => ({}))) as { error?: unknown };
  return typeof body.error === "string" ? body.error : fallback;
}

export async function uploadObservationImages(input: {
  rdId: string;
  noteId: string;
  files: File[];
}): Promise<ObservationImageData[]> {
  if (input.files.length === 0) return [];

  // Auth rides Clerk's same-origin session cookie — the same transport
  // authenticatedApi uses — because these calls need the parsed error body.
  const uploadIntentResponse = await fetch(
    `${observationImagesPath(input.rdId, input.noteId)}/upload-intents`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        files: input.files.map((file) => ({
          contentType: file.type,
          byteSize: file.size,
        })),
      }),
    },
  );

  if (!uploadIntentResponse.ok) {
    throw new Error(
      await readApiError(uploadIntentResponse, "Failed to prepare image upload."),
    );
  }

  const intentBody = (await uploadIntentResponse.json()) as UploadIntentResponse;
  const uploads = intentBody.data?.uploads ?? [];
  if (uploads.length !== input.files.length) {
    throw new Error("Failed to prepare every image upload.");
  }

  await Promise.all(
    uploads.map(async (upload, index) => {
      const file = input.files[index];
      if (!file) throw new Error("Failed to match image upload intent.");

      const response = await fetch(upload.uploadUrl, {
        method: upload.method,
        headers: upload.headers,
        body: file,
      });
      if (!response.ok) {
        throw new Error(`Failed to upload image ${index + 1}.`);
      }
    }),
  );

  const completeResponse = await fetch(
    `${observationImagesPath(input.rdId, input.noteId)}/complete`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        images: uploads.map((upload, index) => ({
          id: upload.id,
          contentType: input.files[index]?.type,
        })),
      }),
    },
  );

  if (!completeResponse.ok) {
    throw new Error(
      await readApiError(completeResponse, "Failed to finish image upload."),
    );
  }

  const completeBody = (await completeResponse.json()) as CompleteImagesResponse;
  return completeBody.data ?? [];
}
